/**
 * Run lifecycle core.
 *
 * One exhaustive status-to-phase table per run kind answers "is this run still
 * moving, is it finished, may the operator retry it". Web hooks read their poll
 * cadence from it, screens read retry and in-flight state from it, and the
 * backend derives its terminal / in-flight sets from it, so the copies cannot
 * drift apart again.
 *
 * Plan: docs/plans/2026-10-01-2026-refactor-web-04-run-lifecycle-module-plan.md (U1)
 */

/**
 * - `active`: work is moving and a poller should watch it.
 * - `resting`: not finished, but nothing moves until a person or another actor
 *   does something. Folding this into `settled` manufactures false finishes;
 *   folding it into `active` polls forever.
 * - `settled`: finished, successfully or not.
 */
export type RunPhase = 'active' | 'resting' | 'settled'

export interface RunLifecycle<S extends string> {
  phase(status: S): RunPhase
  /** `phase === 'active'`. A missing status is not in flight. */
  isInFlight(status: S | null | undefined): boolean
  /** `phase === 'settled'`. */
  isTerminal(status: S): boolean
  canRetry(status: S): boolean
  /** Statuses in one phase, in table order. For SQL IN-lists and table tests. */
  statusesIn(phase: RunPhase): readonly S[]
}

/**
 * Build a lifecycle from a table. Pass the status type explicitly
 * (`defineRunLifecycle<MyStatus>({...})`): `phases` is then a
 * `Record<S, RunPhase>`, so a status missing from the table fails typecheck.
 */
export function defineRunLifecycle<S extends string>(spec: {
  phases: Record<S, RunPhase>
  retryable?: readonly S[]
}): RunLifecycle<S> {
  const { phases } = spec
  const retryable: ReadonlySet<string> = new Set(spec.retryable ?? [])
  const statuses = Object.keys(phases) as S[]
  const byPhase: Record<RunPhase, readonly S[]> = {
    active: statuses.filter(s => phases[s] === 'active'),
    resting: statuses.filter(s => phases[s] === 'resting'),
    settled: statuses.filter(s => phases[s] === 'settled')
  }
  return {
    phase: status => phases[status],
    isInFlight: status => status != null && phases[status] === 'active',
    isTerminal: status => phases[status] === 'settled',
    canRetry: status => retryable.has(status),
    statusesIn: phase => byPhase[phase]
  }
}
